import {InspectionDraftsService} from './inspection-drafts.service';
import {TranslateService} from '@ngx-translate/core';
import { Inject, Injectable, makeStateKey, Optional, PLATFORM_ID, TransferState } from '@angular/core';
import { isPlatformBrowser } from "@angular/common";
import { StorageService } from "./storage.service";
import { AuthApiModel } from "../../api/models/auth.api.model";
import { plainToClass, plainToInstance } from "class-transformer";
import { CurrentUserViewModel } from "../models/current-user.view-model";
import { BehaviorSubject, defer, EMPTY, from, Observable, of, ReplaySubject, Subject } from "rxjs";
import { catchError, concatMap, finalize, map, shareReplay, take, takeUntil, tap } from "rxjs/operators";
import { CredentialsApiModel } from "../../api/models/credentials.api.model";
import { AuthenticationRestService } from "../../api/services/authentication.rest.service";
import { NgxRolesService } from "ngx-permissions";
import { MediaObjectRestService } from "../../api/services/media-object.rest.service";
import { MediaObjectApiModel } from "../../api/models/media-object.api-model";
import { MediaObjectViewModel } from "../models/media-object.view-model";
import { QueryParamsApiModel } from "../../api/models/query-params-api.model";
import { FilterParameterApiModel } from "../../api/models/filter-parameter.api.model";
import { ResultListApiModel } from "../../api/models/result-list.api.model";
import { Oauth2UserApiModel } from "../../api/models/oauth2-user.api.model";
import { FacebookService } from "./facebook.service";
import { UserApiModel } from "../../api/models/user.api-model";
import { FacebookLoginResponse } from "@capgo/capacitor-social-login/dist/esm/definitions";
import { GoogleLoginResult, GoogleService } from "./google.service";
import { JwtService } from "./jwt.service";
import { TokenApiModel } from "../../api/models/token.api.model";
import { REQUEST } from "../providers/http-context-providers";
import type { Request } from 'express';
import {CacheStoreService} from "./cache-store.service";
import {EventSourceService} from "./event-source.service";

export type SessionState = 'restoring' | 'authenticated' | 'guest' | 'unavailable';
export const ANONYMOUS_SSR_SESSION = makeStateKey<boolean>('wtg-anonymous-ssr-session');

@Injectable({
  providedIn: 'root'
})
export class TokenDataProviderService {

  get currentUser(): Observable<CurrentUserViewModel> {
    if (!this.isLoggedInSSR() && !this.isLoggedIn()) {
      return of(new CurrentUserViewModel(new UserApiModel()));
    }
    if (this.hasFetchedCurrentUser) {
      return this.user$;
    }
    return this.refreshUser();
  }

  get oAuth2User(): Oauth2UserApiModel {
    return plainToClass(Oauth2UserApiModel, this.getJwtUser());
  }

  set currentUser(currentUserViewModelObservable: Observable<CurrentUserViewModel>) {
    currentUserViewModelObservable.subscribe((currentUserViewModel: CurrentUserViewModel) => {
      this.userSource.next(currentUserViewModel);
      this.hasFetchedCurrentUser = true;
      this.sessionStateSource.next(currentUserViewModel?.['@id'] ? 'authenticated' : 'guest');
    });
  }

  private userSource = new BehaviorSubject<CurrentUserViewModel>(new CurrentUserViewModel(this.getJwtUser()));
  public user$: Observable<CurrentUserViewModel>;
  private readonly sessionChangedSource = new Subject<void>();
  public readonly sessionChanged$ = this.sessionChangedSource.asObservable();
  private currentUserRefresh$?: Observable<CurrentUserViewModel>;
  private hasFetchedCurrentUser = false;
  private readonly sessionStateSource = new BehaviorSubject<SessionState>('restoring');
  public readonly sessionState$ = this.sessionStateSource.asObservable();
  private readonly browserReadySource = new ReplaySubject<void>(1);
  public readonly browserReady$ = this.browserReadySource.asObservable();
  public isBrowserReady = false;

  public get hasAnonymousSsrSession(): boolean {
    return this.transferState?.get(ANONYMOUS_SSR_SESSION, false) || false;
  }

  public get sessionState(): SessionState {
    return this.sessionStateSource.value;
  }

  constructor(
    private jwt: JwtService,
    private storage: StorageService,
    private as: AuthenticationRestService,
    private mo: MediaObjectRestService,
    private rs: NgxRolesService,
    private fs: FacebookService,
    private gs: GoogleService,
    private cacheStore: CacheStoreService,
    private eventSourceService: EventSourceService,
    private inspectionDrafts: InspectionDraftsService,
    private inspectionTranslate: TranslateService,
    @Inject(PLATFORM_ID) private platformId: object,
    @Optional() @Inject(REQUEST) private request?: Request,
    @Optional() private readonly transferState?: TransferState,
  ) {
    this.user$ = this.userSource.asObservable();
  }

  public getJwtUser(): UserApiModel {
    if (isPlatformBrowser(this.platformId)) {
      const jwtToken = this.storage.getItem('token');
      if (jwtToken) {
        const data = this.jwt.decodeToken(jwtToken);
        const user = plainToInstance(UserApiModel, data);
        this.rs.flushRoles();
        this.loadRoles(user);
        return user;
      }
    }
    return new UserApiModel();
  }

  public isLoggedIn(): boolean {
    if (isPlatformBrowser(this.platformId)) {
      const hasValidToken = !this.jwt.isTokenExpired();
      if (hasValidToken) {
        return true;
      }

      if (!this.jwt.hasRemember()) {
        return false;
      }

      return this.jwt.canRefresh();
    }

    return false;
  }

  public isAuthenticatedFully(): boolean {
    if (isPlatformBrowser(this.platformId)) {
      return this.isLoggedIn() && !!this.getJwtUser().completedAt && !!this.getJwtUser().enabled;
    }

    return false;
  }

  public requiresRegistrationCompletion(): boolean {
    if (isPlatformBrowser(this.platformId)) {
      const user = this.getJwtUser();

      return this.isLoggedIn()
        && !user.completedAt
        && !user.username;
    }

    return false;
  }

  public isEnabled(): boolean {
    if (isPlatformBrowser(this.platformId)) {
      return this.isLoggedIn() && !!this.getJwtUser().enabled;
    }

    return false;
  }

  public login(credentials: CredentialsApiModel): Observable<AuthApiModel> {
    return this.as.login(credentials)
      .pipe(tap((response: AuthApiModel) => {
        this.jwt.setSession(response, credentials.remember);
        this.handleSessionChange();
        this.userSource.next(new CurrentUserViewModel(this.getJwtUser()))
      }));
  }

  public loginFromInvitation(token: TokenApiModel): Observable<AuthApiModel> {
    return this.as.loginFromInvitation(token)
      .pipe(tap((response: AuthApiModel) => {
        this.jwt.setSession(response, false);
        this.handleSessionChange();
        void this.cacheStore.deleteByPrefix('/api/notifications');
        void this.cacheStore.deleteByPrefix('/api/projects');
        this.userSource.next(new CurrentUserViewModel(this.getJwtUser()))
      }));
  }

  public loginFromOwnerChangeInvitation(token: TokenApiModel): Observable<AuthApiModel> {
    return this.as.loginFromOwnerChangeInvitation(token)
      .pipe(tap((response: AuthApiModel) => {
        this.jwt.setSession(response, false);
        this.handleSessionChange();
        void this.cacheStore.deleteByPrefix('/api/notifications');
        void this.cacheStore.deleteByPrefix('/api/projects');
        this.userSource.next(new CurrentUserViewModel(this.getJwtUser()))
      }));
  }

  public fbLogin(credentials: CredentialsApiModel, limited: boolean) {
    return from(this.fs.loginWithFacebook(limited))
      .pipe(
        concatMap(({ result }) => {
          if (!result) return EMPTY;
          console.log({ limited });
          return of(result);
        }),
        concatMap((fbLoginResponse: FacebookLoginResponse) => this.as.fbLogin({ limitedLogin: limited, payload: fbLoginResponse })),
        tap((response: AuthApiModel) => {
          this.jwt.setSession(response, credentials.remember);
          this.handleSessionChange();
          this.userSource.next(new CurrentUserViewModel(this.getJwtUser()))
        })
      );
  }

  public googleLogin(credentials: CredentialsApiModel, limited: boolean = true) {
    return from(this.gs.login(limited))
      .pipe(
        concatMap(({ result }: { result: GoogleLoginResult }) => {
          if (!result) return EMPTY;
          return of(result);
        }),
        concatMap((googleLoginResponse: GoogleLoginResult) => this.as.googleLogin({ limitedLogin: limited, payload: googleLoginResponse })),
        tap((response: AuthApiModel) => {
          this.jwt.setSession(response, credentials.remember);
          this.handleSessionChange();
          this.userSource.next(new CurrentUserViewModel(this.getJwtUser()))
        })
      );
  }

  private handleSessionChange(): void {
    this.hasFetchedCurrentUser = false;
    this.currentUserRefresh$ = undefined;
    this.sessionStateSource.next('restoring');
    this.sessionChangedSource.next();
  }

  /** Called after the first browser render; SSR's anonymous resolver is only initial data. */
  public restoreSession(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    this.isBrowserReady = true;
    this.transferState?.remove(ANONYMOUS_SSR_SESSION);
    this.browserReadySource.next();
    const started = performance.now();
    this.jwt.getTokenFromStorage();
    if (!this.isLoggedIn()) {
      this.rs.flushRoles();
      this.userSource.next(new CurrentUserViewModel(new UserApiModel()));
      this.sessionStateSource.next('guest');
      this.recordRestorationTime(started);
      return;
    }
    this.currentUser.pipe(
      take(1),
      tap(() => this.sessionStateSource.next('authenticated')),
      catchError(() => EMPTY),
      finalize(() => this.recordRestorationTime(started)),
    ).subscribe();
  }

  private recordRestorationTime(started: number): void {
    performance.measure('wtg-session-restore', {start: started, end: performance.now()});
  }

  public refreshUser(): Observable<CurrentUserViewModel> {
    if (this.currentUserRefresh$) return this.currentUserRefresh$;
    let started: number;
    const request$ = defer(() => {
      started = performance.now();
      if (isPlatformBrowser(this.platformId)) this.sessionStateSource.next('restoring');
      return this.as.getCurrentUser(true);
    }).pipe(
      takeUntil(this.sessionChanged$),
      map((userApiModel: UserApiModel) => {
        const currentUserVM = new CurrentUserViewModel(userApiModel);
        this.loadRoles(userApiModel);
        this.hasFetchedCurrentUser = true;
        this.userSource.next(currentUserVM);
        this.sessionStateSource.next('authenticated');
        const params = new QueryParamsApiModel();
        if (userApiModel.avatar) {
          params.filters.push(new FilterParameterApiModel('id', userApiModel.avatar));
        }
        if (userApiModel.background) {
          params.filters.push(new FilterParameterApiModel('id', userApiModel.background));
        }
        if (params.filters.length) {
          this.mo.getMediaObjectCollection(params)
            .pipe(take(1), takeUntil(this.sessionChanged$), catchError(() => EMPTY))
            .subscribe((mediaObjectResultList: ResultListApiModel<MediaObjectApiModel>) => {
              if (this.userSource.value !== currentUserVM) return;
              const avatar = mediaObjectResultList.records.find(e => e['@id'] === userApiModel.avatar);
              const background = mediaObjectResultList.records.find(e => e['@id'] === userApiModel.background);
              currentUserVM.avatar = new MediaObjectViewModel(avatar);
              currentUserVM.background = new MediaObjectViewModel(background);
              this.userSource.next(currentUserVM);
            });
        }
        return currentUserVM;
      }),
      tap({error: () => {
        this.hasFetchedCurrentUser = false;
        if (isPlatformBrowser(this.platformId) && this.isLoggedIn()) {
          this.sessionStateSource.next('unavailable');
        }
      }}),
      finalize(() => {
        if (this.currentUserRefresh$ === request$) this.currentUserRefresh$ = undefined;
        if (isPlatformBrowser(this.platformId)) {
          performance.measure('wtg-current-user', {start: started, end: performance.now()});
        }
      }),
      shareReplay({bufferSize: 1, refCount: false}),
    );
    this.currentUserRefresh$ = request$;
    return request$;
  }

  private loadRoles(userApiModel: UserApiModel): void {
    userApiModel.roles?.forEach((e) => this.rs.addRole(e, []));
  }

  public logout(force = false): void {
    if (!force && isPlatformBrowser(this.platformId) && this.inspectionDrafts.pending
        && !window.confirm(this.inspectionTranslate.instant('inspections.logoutWarning'))) { return; }
    void this.inspectionDrafts.clear();
    this.jwt.clearAllTokenRelatedFromStorage();
    this.rs.flushRoles();
    this.hasFetchedCurrentUser = false;
    this.currentUserRefresh$ = undefined;
    this.sessionStateSource.next('guest');
    this.userSource.next(new CurrentUserViewModel(new UserApiModel()));
    this.sessionChangedSource.next();
    void this.cacheStore.clear();
    this.eventSourceService.closeAll();
    //this.fs.logout();
  }

  public isLoggedWithRememberOption(): boolean {
    return this.jwt.hasRemember();
  }

  getTokenFromSSR(): string | null {
    const cookie = this.request?.headers?.cookie;
    if (!cookie) return null;

    return cookie.match(/AUTH_TOKEN=([^;]+)/)?.[1] ?? null;
  }

  public isLoggedInSSR(): boolean {
    return !!this.getTokenFromSSR();
  }
}
